import React, { useEffect, useRef, useState } from 'react';

// #1451 -- DECISION LOCKED (Shane, 2026-08-07): subtle persona-switch visual
// signal. Real finding: switched personas while sitting in Lobby (not the
// dashboard) and there was no clear visual signal the persona had changed.
// Real ask: a light, subtle glow/transition effect on persona switch, so
// it's visually obvious even when not looking at the dashboard specifically.
//
// Mounted once at the Shell root (App.js) so it fires no matter which page
// is showing when the switch happens -- Lobby, dashboard, anywhere.
//
// Deliberately keys off `--partner-color` (the same CSS custom property
// applyPartnerVars() already sets on document.documentElement for every
// other brand-colored UI element, e.g. Lobby's Open/Park/Remove buttons)
// rather than re-deriving color from the partner object -- one real source
// of truth, no drift between this and anything else that reads brand color.
//
// `switchSignal` comes from AppContext's personaSwitchSignal -- a counter
// bumped ONLY inside chooseActivePartner (the explicit, user-initiated
// pick), never on boot-time session restore. That distinction already
// exists in AppContext for real, unrelated reasons (viewMode reset) --
// reusing it here means a page reload / restored session can never
// trigger a spurious glow, only a real switch can.
export default function PersonaSwitchGlow({ switchSignal }) {
  const [flashing, setFlashing] = useState(false);
  const prevSignalRef = useRef(switchSignal);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const prevSignal = prevSignalRef.current;
    prevSignalRef.current = switchSignal;

    // Initial mount, or no real signal change -- do not flash.
    if (switchSignal === prevSignal) return;

    setFlashing(false);
    // Restart the CSS animation cleanly even if a switch lands mid-flash
    // (rapid persona switching shouldn't leave a stuck or truncated glow).
    const raf = requestAnimationFrame(() => setFlashing(true));

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setFlashing(false), 1100);

    return () => {
      cancelAnimationFrame(raf);
    };
  }, [switchSignal]);

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  if (!flashing) return null;

  return (
    <div
      aria-hidden="true"
      className="persona-switch-glow"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        pointerEvents: 'none',
        boxShadow: 'inset 0 0 0 3px var(--partner-color, #0077cc), inset 0 0 60px 10px var(--partner-color, #0077cc)',
        opacity: flashing ? 1 : 0,
        animation: 'persona-switch-glow-fade 1.1s ease-out forwards',
      }}
    >
      <style>{`
        @keyframes persona-switch-glow-fade {
          0%   { opacity: 0; }
          12%  { opacity: 0.55; }
          100% { opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .persona-switch-glow { animation: none !important; opacity: 0 !important; }
        }
      `}</style>
    </div>
  );
}
